/*!
 * egt.select.capped — capped multi-select control (app-scoped).
 *
 * A multi-select where the user may pick at most `max` options. Once the cap
 * is reached, unchecked options are disabled (and a hint shows); validation
 * fails if somehow exceeded. Modeled on uat/src/elements/ua.select.multiple.js
 * (the same checkbox/list/chip pattern) with an added `max` field + cap logic.
 *
 * save()  -> array of selected values (length <= max)
 * load()  -> sets checked state from an array (or comma string)
 *
 * Renders raw DOM inputs (NOT nested <uae>) so it presents a single
 * uae[name] boundary the wizard's _collectDocument serializes via this save().
 */

var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.select.capped",
  title: "Capped Multi Select",
  description: "Select up to a maximum number of options from a list.",
  control: true,
  preview: "ua.preview.select.multiple",
  supportServer: false,
  fields: {
    name: {
      title: "Name",
      type: "string",
      description: "Form field name.",
      required: true
    },
    title: {
      title: "Title",
      type: "string",
      description: "Control title."
    },
    description: {
      title: "Description",
      type: "inner",
      description: "Helper text."
    },
    required: {
      title: "Required",
      type: "boolean",
      description: "Whether at least one value is required.",
      default: false
    },
    max: {
      title: "Max",
      type: "number",
      description: "Maximum number of options the user may select.",
      default: 3
    },
    options: {
      title: "Options",
      type: "array",
      of: "object",
      description: "Option rows: { value, label, description }."
    },
    "options-json": {
      title: "Options JSON",
      type: "string",
      description: "JSON string for options rows (UA/JSON-safe alternative to options)."
    },
    value: {
      title: "Value",
      type: "any",
      description: "Selected values (array or comma string)."
    }
  }
});

function escapeHtml(str) {
  return (str || "").toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function normalizeOptions(options) {
  var rows = options instanceof Array ? options : [];
  var out = [];
  for (var i = 0; i < rows.length; i++) {
    var row = rows[i] || {};
    var value = (row.value || "").toString().trim();
    if (!value) continue;
    out.push({
      value: value,
      label: (row.label || row.value || "").toString(),
      description: (row.description || "").toString()
    });
  }
  return out;
}

function normalizeOptionsFromInput(options, optionsJson) {
  if (options instanceof Array && options.length > 0) return normalizeOptions(options);
  var raw = (optionsJson || "").toString().trim();
  if (!raw) return [];
  try {
    return normalizeOptions(JSON.parse(raw));
  } catch (error) {
    console.warn("egt.select.capped: invalid options-json payload.", { error: error });
    return [];
  }
}

function normalizeSelected(value) {
  if (value instanceof Array) {
    return value.map(function (item) { return (item || "").toString().trim(); }).filter(Boolean);
  }
  var raw = (value || "").toString().trim();
  if (!raw) return [];
  return raw.split(",").map(function (item) { return (item || "").toString().trim(); }).filter(Boolean);
}

function resolveMax(value) {
  var n = Number(value);
  if (!Number.isFinite(n) || n < 1) return 3;
  return Math.floor(n);
}

exports.render = async function (options) {
  var name = (options.name || "").toString().trim();
  var title = (options.title || "").toString().trim();
  var description = (options.description || "").toString().trim();
  var required = options.required === true;
  var max = resolveMax(options.max);
  var optionRows = normalizeOptionsFromInput(options.options || [], options["options-json"] || "");
  var selected = normalizeSelected(options.value);

  var wrapper = document.createElement("div");
  wrapper.className = "form-group pb-3 egt-capped";
  wrapper.setAttribute("name", name);
  wrapper.setAttribute("data-required", required ? "true" : "false");
  wrapper.setAttribute("data-max", String(max));

  if (title) {
    var label = document.createElement("label");
    label.className = "form-label fw-semibold";
    label.textContent = title;
    wrapper.appendChild(label);
  }

  var help = document.createElement("div");
  help.className = "form-text mb-2 egt-capped__hint";
  help.textContent = description ? (description + " (Choose up to " + max + ".)") : ("Choose up to " + max + ".");
  wrapper.appendChild(help);

  var selectedMap = {};
  for (var si = 0; si < selected.length; si++) { selectedMap[selected[si]] = true; }

  var list = document.createElement("div");
  list.className = "d-flex flex-column gap-1 egt-capped__list";
  wrapper.appendChild(list);

  for (var i = 0; i < optionRows.length; i++) {
    var row = optionRows[i];
    var item = document.createElement("label");
    item.className = "d-flex align-items-start gap-2 border rounded p-2 egt-capped__opt";
    item.setAttribute("data-capped-option", row.value.toLowerCase());

    var check = document.createElement("input");
    check.type = "checkbox";
    check.className = "visually-hidden";
    check.setAttribute("data-capped-check", "true");
    check.value = row.value;
    check.name = name;
    check.checked = !!selectedMap[row.value];

    var body = document.createElement("div");
    body.className = "flex-grow-1";
    body.innerHTML = "<div class='d-flex flex-column'><span>" + escapeHtml(row.label) + "</span>" +
      (row.description ? ("<small class='text-muted'>" + escapeHtml(row.description) + "</small>") : "") + "</div>";

    item.appendChild(check);
    item.appendChild(body);
    list.appendChild(item);
  }

  var invalid = document.createElement("div");
  invalid.className = "invalid-feedback d-block egt-capped__invalid";
  invalid.textContent = "";
  wrapper.appendChild(invalid);

  function countChecked() {
    return list.querySelectorAll("input[data-capped-check='true']:checked").length;
  }

  function syncState() {
    var checkedCount = countChecked();
    var atCap = checkedCount >= max;
    var items = list.querySelectorAll("[data-capped-option]");
    for (var j = 0; j < items.length; j++) {
      var cb = items[j].querySelector("input[data-capped-check='true']");
      if (!cb) continue;
      var isChecked = !!cb.checked;
      cb.disabled = atCap && !isChecked;
      items[j].classList.toggle("border-primary", isChecked);
      items[j].classList.toggle("egt-capped__opt--on", isChecked);
      items[j].classList.toggle("egt-capped__opt--disabled", cb.disabled);
    }
    if (atCap) {
      invalid.textContent = "";
    }
  }

  list.addEventListener("change", function (event) {
    var target = event.target;
    if (!target || target.getAttribute("data-capped-check") !== "true") return;
    if (target.checked && countChecked() > max) {
      // Should not happen (disabled prevents it) but fail-closed anyway.
      target.checked = false;
    }
    syncState();
  });

  syncState();
  return wrapper;
};

async function save(name, control, repo) {
  var required = control.getAttribute("data-required") === "true";
  var max = resolveMax(control.getAttribute("data-max"));
  var checks = control.querySelectorAll("input[data-capped-check='true']:checked");
  var selected = [];
  for (var i = 0; i < checks.length; i++) {
    var value = (checks[i].value || "").toString().trim();
    if (!value) continue;
    if (selected.indexOf(value) >= 0) continue;
    selected.push(value);
  }

  var invalid = control.querySelector(".egt-capped__invalid");

  if (required && selected.length === 0) {
    repo.success = false;
    repo.errors = [{ type: "validation", message: "Please choose at least one option." }];
    if (invalid) invalid.textContent = "Please choose at least one option.";
    control.classList.add("is-invalid");
    repo.data = [];
    return repo;
  }

  if (selected.length > max) {
    repo.success = false;
    repo.errors = [{ type: "validation", message: "Please choose no more than " + max + " options." }];
    if (invalid) invalid.textContent = "Please choose no more than " + max + " options.";
    control.classList.add("is-invalid");
    repo.data = selected.slice(0, max);
    return repo;
  }

  if (invalid) invalid.textContent = "";
  control.classList.remove("is-invalid");
  repo.success = true;
  repo.data = selected;
  return repo;
}
module.exports.save = save;

async function load(name, control, data) {
  var selected = normalizeSelected(data);
  var checks = control.querySelectorAll("input[data-capped-check='true']");
  for (var i = 0; i < checks.length; i++) {
    checks[i].checked = selected.indexOf(checks[i].value) >= 0;
  }
  var firstCheck = control.querySelector("input[data-capped-check='true']");
  if (firstCheck) {
    firstCheck.dispatchEvent(new Event("change", { bubbles: true }));
  }
}
module.exports.load = load;
